import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import Dialog from "../../components/ui/Dialog";
import DataTable, {
  Column,
  DataTableFetchParams,
  DataTableFilterDef,
} from "../../components/ui/DataTable";
import { listingsService } from "../../services/listings.service";
import { WhatsappListing } from "../../types/listing";
import { getApiPath } from "../../config/api.config";

const formatPrice = (amount: string | null, currency: string | null): string => {
  if (!amount) return "-";
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return `${currency || ""} ${amount}`.trim();

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch {
    return `${currency || ""} ${numeric.toFixed(2)}`.trim();
  }
};

const toPercent = (value: number): string =>
  `${Math.max(0, Math.min(1, value)) * 100}%`;

const getWhatsappSenderLink = (senderNumber: string | null): string | null => {
  if (!senderNumber) return null;
  const normalized = senderNumber.replace(/[^\d]/g, "");
  if (!normalized) return null;
  return `https://wa.me/${normalized}`;
};

const WhatsappListingsPage = () => {
  const { t } = useTranslation("common");
  const [previewItem, setPreviewItem] = useState<WhatsappListing | null>(null);

  const handleFetch = useCallback(async (params: DataTableFetchParams) => {
    const res = await listingsService.listWhatsapp({
      page: params.page,
      take: params.pageSize,
      orderBy: params.sortBy || "postedAt",
      order: params.sortOrder || "DESC",
      search: params.search || undefined,
      sourceId: params.filters?.sourceId || undefined,
    });

    return {
      data: res.data,
      total: res.meta.itemCount,
      totalPages: res.meta.pageCount,
    };
  }, []);

  const columns: Column<WhatsappListing>[] = useMemo(
    () => [
      {
        key: "imageUrl",
        header: t("reference_image"),
        className: "w-32",
        render: (row) => (
          <button
            type="button"
            onClick={() => row.imageUrl && setPreviewItem(row)}
            disabled={!row.imageUrl}
            className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 disabled:cursor-default enabled:cursor-zoom-in enabled:hover:ring-2 enabled:hover:ring-blue-400 transition"
            title={row.imageUrl ? t("click_to_enlarge") : t("no_image")}
          >
            {row.imageUrl ? (
              <>
                <img
                  src={getApiPath(row.imageUrl)}
                  alt={row.cardName || row.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {row.matchRegion ? (
                  <div
                    className="absolute border-2 border-amber-400 bg-amber-300/10 shadow-[0_0_0_9999px_rgba(0,0,0,0.08)] pointer-events-none"
                    style={{
                      left: toPercent(row.matchRegion.x),
                      top: toPercent(row.matchRegion.y),
                      width: toPercent(row.matchRegion.width),
                      height: toPercent(row.matchRegion.height),
                    }}
                  />
                ) : null}
                {row.matchConfidence != null ? (
                  <div className="absolute left-1 bottom-1 px-1.5 py-0.5 rounded bg-black/75 text-white text-[10px] font-medium">
                    {Math.round(row.matchConfidence * 100)}%
                  </div>
                ) : null}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs text-gray-400 px-2 text-center">
                {t("no_image")}
              </div>
            )}
          </button>
        ),
      },
      {
        key: "cardName",
        header: t("card_name"),
        sortable: true,
        className: "w-[11rem] min-w-[11rem] max-w-[11rem]",
        render: (row) => (
          <div className="space-y-1 w-[11rem]">
            <div className="font-medium text-gray-900 dark:text-gray-100 line-clamp-2 whitespace-normal break-words">
              {row.cardName || row.title}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-normal break-words">
              {row.sourceName || row.sourceId}
            </div>
          </div>
        ),
      },
      {
        key: "priceAmount",
        header: t("price"),
        className: "whitespace-nowrap font-semibold",
        render: (row) => formatPrice(row.priceAmount, row.currency),
      },
      {
        key: "originalMessage",
        header: t("original_message"),
        className: "min-w-[24rem] max-w-[36rem]",
        render: (row) => (
          <div className="space-y-1">
            <div className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap line-clamp-4">
              {row.originalMessage || "-"}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              <span>
                {t("sender")}: {row.senderName || "-"}
              </span>
              <span>
                {t("sender_number")}: {row.senderNumber || "-"}
              </span>
              <span>
                {t("chat")}: {row.chatName || "-"}
              </span>
            </div>
          </div>
        ),
      },
      {
        key: "sourceUrl",
        header: t("source_link"),
        className: "min-w-[10rem]",
        render: (row) => {
          const senderLink = getWhatsappSenderLink(row.senderNumber);
          if (!senderLink) return <span className="text-gray-400">-</span>;

          return (
            <a
              href={senderLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 items-center justify-center whitespace-nowrap rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
            >
              {t("message_sender")}
            </a>
          );
        },
      },
      {
        key: "postedAt",
        header: t("created_at"),
        sortable: true,
        className: "whitespace-nowrap",
        render: (row) =>
          new Date(row.postedAt || row.createdAt).toLocaleString(),
      },
    ],
    [t],
  );

  const filterDefs: DataTableFilterDef[] = useMemo(
    () => [{ key: "sourceId", label: t("source"), type: "text" }],
    [t],
  );

  return (
    <Layout title={t("whatsapp_listings")}>
      <div className="space-y-4 h-full flex flex-col">
        <Card className="p-4 shadow-sm flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                {t("whatsapp_listings")}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t("whatsapp_listings_hint")}
              </p>
            </div>
          </div>
        </Card>

        <DataTable
          className="flex-1 min-h-0"
          columns={columns}
          onFetch={handleFetch}
          filterDefs={filterDefs}
          initialState={{
            sortBy: "postedAt",
            sortOrder: "DESC",
            pageSize: 25,
          }}
          emptyMessage={t("noData")}
        />
      </div>
      <Dialog
        open={Boolean(previewItem)}
        title={previewItem?.cardName || previewItem?.title || t("preview_image")}
        onClose={() => setPreviewItem(null)}
        panelClassName="max-w-[90vw] lg:max-w-[88vw]"
      >
        {previewItem?.imageUrl ? (
          <div className="space-y-3">
            <div className="relative w-full rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900">
              <img
                src={getApiPath(previewItem.imageUrl)}
                alt={previewItem.cardName || previewItem.title}
                className="w-full h-auto max-h-[82vh] object-contain"
              />
              {previewItem.matchRegion ? (
                <div
                  className="absolute border-4 border-amber-400 bg-amber-300/10 shadow-[0_0_0_9999px_rgba(0,0,0,0.12)] pointer-events-none"
                  style={{
                    left: toPercent(previewItem.matchRegion.x),
                    top: toPercent(previewItem.matchRegion.y),
                    width: toPercent(previewItem.matchRegion.width),
                    height: toPercent(previewItem.matchRegion.height),
                  }}
                />
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
              <span>
                {t("card_name")}: {previewItem.cardName || previewItem.title}
              </span>
              <span>
                {t("price")}:{" "}
                {formatPrice(previewItem.priceAmount, previewItem.currency)}
              </span>
              {previewItem.matchConfidence != null ? (
                <span>
                  {t("confidence")}: {Math.round(previewItem.matchConfidence * 100)}%
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </Dialog>
    </Layout>
  );
};

export default WhatsappListingsPage;
